import React from 'react';
import A, { AnchorProps } from '@sls-react/components/Links/A';
import extractPathname from '@sls-react/utils/extractPathname';
import { Link, useRouter } from '@sls-react/routing';
import { RailsFlattenedFacetQuery } from '@sls-react/typings/services/rails/facets';
import qsStringify from '@sls-react/utils/qsStringify';
import getEnv from '@sls-react/utils/getEnv';

type Props = AnchorProps & {
  // Use the internal router rather than a hard link
  internalRouter?: boolean;
  path: string;
  query?: RailsFlattenedFacetQuery;
  type?: 'shop' | 'sm' | 'nav';
};

const ShopLink: React.FC<Props> = ({
  children,
  internalRouter,
  path,
  query,
  type = 'shop',
  ...props
}) => {
  const { locale } = useRouter();
  const stringifiedQuery = qsStringify(query);
  const anchorProps: React.AnchorHTMLAttributes<HTMLAnchorElement> = {};

  const keys = query ? Object.keys(query) : [];

  if (query && keys.length > 0) {
    if (!query.page) {
      anchorProps.rel = 'nofollow noindex';
    }
  }

  if (!path) {
    let href = `/search${stringifiedQuery}`;
    if (locale !== 'en-US') {
      href = `/${locale}${href}`;
    }

    return (
      <A href={href} {...anchorProps} {...props}>
        {children}
      </A>
    );
  }

  let fullPath: string;

  if (path.startsWith('http')) {
    const extracted = extractPathname(path);
    if (!extracted.startsWith(`/${type}`)) {
      const split = extracted.split('/');
      split.splice(1, 1, type);
      fullPath = split.join('/') + stringifiedQuery;
    } else {
      fullPath = extracted + stringifiedQuery;
    }
  } else if (path.startsWith(`/${type}`)) {
    fullPath = path + stringifiedQuery;
  } else if (path.startsWith(type)) {
    fullPath = `/${path}${stringifiedQuery}`;
  } else {
    fullPath = `/${type}/${path}${stringifiedQuery}`;
  }

  if (locale !== 'en-US') {
    fullPath = `/${locale}${fullPath}`;
  }

  if (internalRouter) {
    return (
      <Link href="/shop/[...lander]" as={fullPath}>
        <A {...anchorProps} {...props}>
          {children}
        </A>
      </Link>
    );
  }

  return (
    <A {...anchorProps} {...props} href={fullPath} rel="noindex">
      {children}
    </A>
  );
};

export default ShopLink;
